<template>
  <div class="rp-page">
    <input type="file" ref="avatarInput" @change="handleAvatarChange" style="display:none" accept="image/*" />
    <AvatarCropperDialog :file="cropFile" @done="onCropDone" @cancel="cropFile = null" />


    <!-- ─── Banknote Profile (Hero) ─── -->
    <div v-if="!isLoading && profile" class="mt-6 mb-2 mx-auto px-4" style="max-width: 1200px;">
      <BanknoteAvatarDialog
        is-static
        show-upgrade
        show-downline-button
        downline-role="rider"
        :avatar-url="avatarPreviewUrl || fullImageUrl(profile.profileImage)"
        :initials="userInitials"
        :pack-name="profile.commissionPack && profile.commissionPack.name"
        :user-ref="profile.commisionRefCode && profile.commisionRefCode.refCode"
        :referrer-ref="profile.referrer && profile.referrer.refCode"
        :referrer-name="profile.referrer && profile.referrer.name"
        :referrer-pack-name="profile.referrer && profile.referrer.packName"
        :alter-text="profile.alterText"
        @upgrade="openUpgradeDialog"
        @change="editAvatar()"
        @save-alter="onSaveAlter"
      />
    </div>

    <!-- ─── Body ─── -->
    <div class="rp-body">

      <!-- Loading overlay -->
      <div v-if="isLoading" class="rp-loading-wrapper">
        <div v-for="n in 3" :key="n" class="rp-skeleton-card">
          <div class="rp-sk__header"></div>
          <div class="rp-sk__line rp-sk__line--long"></div>
          <div class="rp-sk__line rp-sk__line--med"></div>
        </div>
      </div>

      <template v-else>
        <CommissionPackSection ref="packSection" :current-pack="profile.commissionPack || null"
          :wallet-balance="walletBalance" role="rider" style="display:none" @pack-changed="onPackChanged" />

        <!-- ── ข้อมูลส่วนตัว ── -->
        <div class="rp-section">
          <div class="rp-section__header">
            <div class="rp-section__title">
              <v-icon size="18" color="#1D4ED8" class="mr-2">mdi-account-outline</v-icon>
              ข้อมูลส่วนตัว
            </div>
            <span class="rp-section__note">แก้ไขได้เฉพาะ อีเมล / เบอร์โทร</span>
          </div>

          <div class="rp-card">
            <!-- Read-only fields -->
            <div class="rp-field-row rp-field-row--disabled">
              <div class="rp-field-lbl">ชื่อ</div>
              <div class="rp-field-val">{{ profile.firstName || '—' }}</div>
            </div>
            <div class="rp-field-row rp-field-row--disabled">
              <div class="rp-field-lbl">นามสกุล</div>
              <div class="rp-field-val">{{ profile.lastName || '—' }}</div>
            </div>
            <div class="rp-field-row rp-field-row--disabled">
              <div class="rp-field-lbl">เลขบัตรประชาชน</div>
              <div class="rp-field-val">{{ profile.natId_nationalId || '—' }}</div>
            </div>
            <div class="rp-field-row rp-field-row--disabled rp-field-row--noborder">
              <div class="rp-field-lbl">ที่อยู่</div>
              <div class="rp-field-val rp-field-val--sm">{{ profile.addressDetail || '—' }}</div>
            </div>

            <div class="rp-divider"></div>

            <!-- Editable fields -->
            <div class="rp-field-input">
              <label class="rp-label">อีเมล</label>
              <input v-model="profile.email" type="email" class="rp-input" placeholder="กรอกอีเมลของคุณ" />
            </div>
            <div class="rp-field-input rp-field-input--noborder">
              <label class="rp-label">เบอร์โทรศัพท์</label>
              <input v-model="profile.phone" type="tel" class="rp-input" placeholder="กรอกเบอร์โทรของคุณ" />
            </div>

            <button class="rp-save-btn" :disabled="savingProfile" @click="saveProfile">
              <v-icon v-if="!savingProfile" size="16" color="white" class="mr-1">mdi-content-save</v-icon>
              <v-progress-circular v-else indeterminate size="16" width="2" color="white"
                class="mr-2"></v-progress-circular>
              {{ savingProfile ? 'กำลังบันทึก...' : 'บันทึกข้อมูล' }}
            </button>
          </div>
        </div>

        <!-- ── ข้อมูลยานพาหนะ ── -->
        <div class="rp-section">
          <div class="rp-section__header">
            <div class="rp-section__title">
              <v-icon size="18" color="#D97706" class="mr-2">mdi-motorbike</v-icon>
              ข้อมูลยานพาหนะ
            </div>
          </div>
          <div class="rp-card">
            <template v-if="profile.vehicles && profile.vehicles.length > 0">
              <div class="rp-field-row">
                <div class="rp-field-lbl">ประเภท</div>
                <div class="rp-field-val">{{ profile.vehicles[0].vehicleType || '—' }}</div>
              </div>
              <div class="rp-field-row">
                <div class="rp-field-lbl">ยี่ห้อ / รุ่น</div>
                <div class="rp-field-val">{{ profile.vehicles[0].vehicleBrand }} {{ profile.vehicles[0].vehicleModel }}
                </div>
              </div>
              <div class="rp-field-row">
                <div class="rp-field-lbl">ทะเบียน</div>
                <div class="rp-field-val">
                  <span class="rp-plate">{{ profile.vehicles[0].vehiclePlate || '—' }}</span>
                </div>
              </div>
              <div class="rp-field-row rp-field-row--noborder">
                <div class="rp-field-lbl">สี</div>
                <div class="rp-field-val">{{ profile.vehicles[0].vehicleColor || '—' }}</div>
              </div>
            </template>
            <div v-else class="rp-empty-row">ไม่มีข้อมูลยานพาหนะ</div>
          </div>
        </div>

        <!-- ── บัญชีธนาคาร ── -->
        <div class="rp-section">
          <div class="rp-section__header">
            <div class="rp-section__title">
              <v-icon size="18" color="#059669" class="mr-2">mdi-bank-outline</v-icon>
              บัญชีธนาคาร
            </div>
          </div>
          <div class="rp-card">
            <div class="rp-field-input">
              <label class="rp-label">ธนาคาร</label>
              <select v-model="profile.bank.bankName" class="rp-input rp-select">
                <option value="">-- เลือกธนาคาร --</option>
                <option v-for="b in banks" :key="b" :value="b">{{ b }}</option>
              </select>
            </div>
            <div class="rp-field-input">
              <label class="rp-label">เลขบัญชี</label>
              <input v-model="profile.bank.bankAccountNumber" type="text" class="rp-input" placeholder="กรอกเลขบัญชี" />
            </div>
            <div class="rp-field-input rp-field-input--noborder">
              <label class="rp-label">ชื่อบัญชี</label>
              <input v-model="profile.bank.bankAccountName" type="text" class="rp-input" placeholder="กรอกชื่อบัญชี" />
            </div>
            <button class="rp-save-btn rp-save-btn--green" @click="saveBankAccount">
              <v-icon size="16" color="white" class="mr-1">mdi-content-save</v-icon>
              บันทึกบัญชีธนาคาร
            </button>
          </div>
        </div>

        <!-- ── PromptPay ── -->
        <div class="rp-section">
          <div class="rp-section__header">
            <div class="rp-section__title">
              <v-icon size="18" color="#6D28D9" class="mr-2">mdi-qrcode-scan</v-icon>
              PromptPay
            </div>
          </div>
          <PromptPayCard :data="profile" :loading="savingPromptPay" @save="handleSavePromptPay" />
        </div>

        <!-- ── รีวิวจากลูกค้า ── -->
        <div class="rp-section">
          <div class="rp-section__header">
            <div class="rp-section__title">
              <v-icon size="18" color="#F59E0B" class="mr-2">mdi-star</v-icon>
              รีวิวจากลูกค้า
            </div>
          </div>
          <div class="rp-card px-8 py-4">
            <!-- Rating Summary -->
            <div class="rp-rating-summary">
              <div class="rp-rating-big">{{ reviewAvg.toFixed(1) }}</div>
              <div class="rp-rating-stars">
                <v-icon v-for="s in 5" :key="s" size="18" :color="s <= Math.round(reviewAvg) ? '#F59E0B' : '#E5E7EB'">
                  mdi-star
                </v-icon>
              </div>
              <div class="rp-rating-count">{{ reviewTotal }} รีวิว</div>
            </div>

            <div v-if="loadingReviews" class="d-flex justify-center py-4">
              <v-progress-circular indeterminate size="24" width="2" color="#F59E0B" />
            </div>

            <div v-else-if="reviews.length === 0" class="rp-empty-row" style="text-align:center; padding: 16px 0;">
              ยังไม่มีรีวิว
            </div>

            <div v-else>
              <div v-for="(rv, i) in reviews" :key="rv.id" class="rp-review-item"
                :class="{ 'rp-review-item--last': i === reviews.length - 1 }">
                <div class="rp-review-top">
                  <div class="rp-review-avatar">
                    <img v-if="rv.reviewer?.profileImage" :src="fullImageUrl(rv.reviewer.profileImage)"
                      class="rp-review-avatar-img" alt="reviewer" />
                    <v-icon v-else size="28" color="#9CA3AF">mdi-account-circle</v-icon>
                  </div>
                  <div class="rp-review-meta">
                    <div class="rp-review-name">{{ rv.reviewer?.username || 'ผู้ใช้งาน' }}</div>
                    <div class="rp-review-stars">
                      <v-icon v-for="s in 5" :key="s" size="13"
                        :color="s <= rv.rating ? '#F59E0B' : '#E5E7EB'">mdi-star</v-icon>
                      <span class="rp-review-date">{{ formatReviewDate(rv.createdAt) }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="rv.comment" class="rp-review-comment">{{ rv.comment }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── เปลี่ยนรหัสผ่าน ── -->
        <div class="rp-section">
          <div class="rp-section__header">
            <div class="rp-section__title">
              <v-icon size="18" color="#DC2626" class="mr-2">mdi-lock-outline</v-icon>
              เปลี่ยนรหัสผ่าน
            </div>
          </div>
          <ProfilePasswordCard ref="passwordCard" :loading="changingPassword" @save="handleSavePassword" />
        </div>
      </template>

      <div style="height: 80px;"></div>
    </div>
  </div>
</template>

<script>
import PromptPayCard from "@/components/Shared/PromptPayCard.vue";
import ProfilePasswordCard from "@/components/AdminProfile/ProfilePasswordCard.vue";
import CommissionPackSection from "@/components/Shared/CommissionPackSection.vue";
import AvatarCropperDialog from "@/components/Shared/AvatarCropperDialog.vue";
import BanknoteAvatarDialog from "@/components/Shared/BanknoteAvatarDialog.vue";
import {
  getRiderProfileApi,
  updateRiderProfileApi,
  updateRiderProfileImageApi,
  changeRiderPasswordApi
} from "@/services/api/auth";
import { getRiderReviewsApi } from "@/services/api/review";
import swalAlert from "@/services/alert/swalServices";
import walletService from "@/services/api/wallet";

export default {
  name: "RiderProfile",
  components: { PromptPayCard, ProfilePasswordCard, CommissionPackSection, AvatarCropperDialog, BanknoteAvatarDialog },

  data() {
    return {
      walletBalance: 0,
      profile: {
        firstName: "",
        lastName: "",
        username: "",
        email: "",
        phone: "",
        natId_nationalId: "",
        addressDetail: "",
        profileImage: null,
        totalDeliveries: 0,
        rating: 0,
        status: "",
        vehicles: [],
        bank: { bankName: "", bankAccountNumber: "", bankAccountName: "" },
        promptPayId: "",
        natId_firstName_en: "",
        natId_lastName_en: "",
      },
      avatarPreviewUrl: null,
      photoDialog: false,
      cropFile: null,
      isLoading: false,
      savingProfile: false,
      savingPromptPay: false,
      changingPassword: false,
      banks: [
        "กสิกรไทย", "กรุงเทพ", "กรุงไทย", "ไทยพาณิชย์", "กรุงศรีอยุธยา",
        "ทหารไทยธนชาต", "ออมสิน", "อาคารสงเคราะห์", "ธ.ก.ส."
      ],
      reviews: [],
      reviewAvg: 0,
      reviewTotal: 0,
      loadingReviews: false,
    };
  },

  computed: {
    vehicleType() {
      return this.profile.vehicles?.[0]?.vehicleType || "—";
    },
    userInitials() {
      if (!this.profile) return "";
      const f = this.profile.firstName || "";
      const l = this.profile.lastName || "";
      return (f.charAt(0) + (l ? l.charAt(0) : "")).toUpperCase();
    },
  },

  created() {
    this.fetchProfile();
    this.fetchWalletBalance();
  },

  watch: {
    'profile.id'(id) {
      if (id) this.fetchReviews(id);
    },
  },

  methods: {
    async fetchWalletBalance() {
      try {
        const res = await walletService.getMyWallet("rider");
        this.walletBalance = res.data?.topUp?.balance ?? 0;
      } catch {
        this.walletBalance = 0;
      }
    },

    onPackChanged(pack) {
      this.profile = { ...this.profile, commissionPack: pack, commissionPackId: pack.id };
      this.fetchWalletBalance();
    },

    async fetchProfile() {
      this.isLoading = true;
      try {
        const { data } = await getRiderProfileApi();
        this.profile = {
          ...data,
          firstName: data.natId_firstName || "",
          lastName: data.natId_lastName || "",
          bank: data.bank || { bankName: "", bankAccountNumber: "", bankAccountName: "" }
        };
        if (data.id) await this.fetchReviews(data.id);
      } catch (error) {
        swalAlert.Error("โหลดข้อมูลไม่สำเร็จ", error?.response?.data?.message);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchReviews(riderId) {
      this.loadingReviews = true;
      try {
        const res = await getRiderReviewsApi(riderId, { take: 10 });
        this.reviews = res.reviews || [];
        this.reviewAvg = res.avg || 0;
        this.reviewTotal = res.count || 0;
      } catch (e) {
        // silently fail — reviews are non-critical
      } finally {
        this.loadingReviews = false;
      }
    },

    async saveProfile() {
      this.savingProfile = true;
      try {
        await updateRiderProfileApi({
          email: this.profile.email,
          phone: this.profile.phone,
        });
        swalAlert.Success("สำเร็จ", "บันทึกข้อมูลส่วนตัวเรียบร้อย");
      } catch (error) {
        swalAlert.Error("บันทึกไม่สำเร็จ", error?.response?.data?.message);
      } finally {
        this.savingProfile = false;
      }
    },

    async saveBankAccount() {
      try {
        await updateRiderProfileApi({
          bankName: this.profile.bank.bankName,
          bankAccountNumber: this.profile.bank.bankAccountNumber,
          bankAccountName: this.profile.bank.bankAccountName,
        });
        swalAlert.Success("สำเร็จ", "บันทึกข้อมูลธนาคารเรียบร้อย");
      } catch (error) {
        swalAlert.Error("บันทึกไม่สำเร็จ", error?.response?.data?.message);
      }
    },

    async handleSavePromptPay(promptPayData) {
      this.savingPromptPay = true;
      try {
        await updateRiderProfileApi(promptPayData);
        swalAlert.Success("สำเร็จ", "บันทึกข้อมูล PromptPay เรียบร้อย");
        this.profile = { ...this.profile, ...promptPayData };
      } catch (error) {
        swalAlert.Error("บันทึกไม่สำเร็จ", error?.response?.data?.message);
      } finally {
        this.savingPromptPay = false;
      }
    },

    async handleSavePassword(passwordData) {
      this.changingPassword = true;
      try {
        await changeRiderPasswordApi(passwordData);
        swalAlert.Success("สำเร็จ", "เปลี่ยนรหัสผ่านเรียบร้อย");
        this.$refs.passwordCard.reset();
      } catch (error) {
        swalAlert.Error("เปลี่ยนรหัสผ่านไม่สำเร็จ", error?.response?.data?.message);
      } finally {
        this.changingPassword = false;
      }
    },

    handleAvatarChange(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) return;
      this.cropFile = file;
    },
    openUpgradeDialog() {
      if (this.$refs.packSection) {
        this.$refs.packSection.openDialog();
      }
    },

    async onCropDone(croppedFile) {
      this.cropFile = null;
      this.avatarPreviewUrl = URL.createObjectURL(croppedFile);
      const formData = new FormData();
      formData.append("profileImage", croppedFile);
      try {
        const res = await updateRiderProfileImageApi(formData);
        if (res?.data?.profileImage) this.profile.profileImage = res.data.profileImage;
        swalAlert.Success("สำเร็จ", "อัปเดตรูปโปรไฟล์เรียบร้อย");
      } catch (error) {
        swalAlert.Error("อัปโหลดไม่สำเร็จ", error?.response?.data?.message);
      }
    },

    fullImageUrl(path) {
      if (!path) return "";
      if (path.startsWith("http") || path.startsWith("blob:")) return path;
      const base = (process.env.VUE_APP_API_URL || "").replace(/\/$/, "");
      const clean = path.startsWith("/") ? path : `/${path}`;
      return base + clean;
    },

    editAvatar() {
      this.$refs.avatarInput.click();
    },

    async onSaveAlter(text) {
      try {
        await updateRiderProfileApi({ alterText: text });
        this.profile.alterText = text;
      } catch (err) {
        console.error("[RIDER SAVE ALTER]", err);
      }
    },

    formatReviewDate(d) {
      if (!d) return "—";
      return new Date(d).toLocaleDateString("th-TH", { year: "numeric", month: "short", day: "numeric" });
    },
  },
};
</script>

<style scoped>
.rp-page {
  min-height: 100vh;
  background: #F1F5F9;
  font-family: 'Sarabun', 'Noto Sans Thai', sans-serif;
  overflow-x: hidden;
}

/* ── Hero ── */
.rp-hero {
  background: linear-gradient(135deg, #1E3A5F 0%, #1E40AF 55%, #2563EB 100%);
  padding: 32px 20px 0;
  border-radius: 0 0 32px 32px;
}

.rp-hero__center {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
}

/* Avatar */
.rp-avatar-wrap {
  position: relative;
  margin-bottom: 14px;
  cursor: pointer;
}

.rp-avatar-circle {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  background: #f1f5f9;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rp-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.rp-avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
}

.rp-avatar-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  border-radius: 50%;
}

.rp-avatar-circle:hover .rp-avatar-overlay {
  opacity: 1;
}

.rp-btn-upload {
  background: #2563eb !important;
  color: white !important;
  text-transform: none;
  border-radius: 8px !important;
}

.rp-hero__name {
  font-size: 20px;
  font-weight: 800;
  color: white;
  letter-spacing: -0.3px;
  margin-bottom: 4px;
}

.rp-hero__username {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 10px;
}

.rp-verified-chip {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #6EE7B7;
  background: rgba(16, 185, 129, 0.15);
  padding: 4px 12px;
  border-radius: 12px;
  border: 1px solid rgba(110, 231, 183, 0.3);
}

/* Stats */
.rp-stats-row {
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 20px 20px 0 0;
  padding: 14px 16px;
}

.rp-stat {
  flex: 1;
  text-align: center;
}

.rp-stat__val {
  font-size: 14px;
  font-weight: 800;
  color: white;
}

.rp-stat__lbl {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.65);
  margin-top: 2px;
}

.rp-stat-divider {
  width: 1px;
  height: 28px;
  background: rgba(255, 255, 255, 0.2);
}

/* ── Body ── */
.rp-body {
  padding: 16px;
}

/* Loading skeleton */
.rp-loading-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rp-skeleton-card {
  background: white;
  border-radius: 18px;
  padding: 20px;
  animation: rp-shimmer 1.5s infinite;
}

.rp-sk__header {
  height: 16px;
  width: 40%;
  background: #E2E8F0;
  border-radius: 8px;
  margin-bottom: 14px;
}

.rp-sk__line {
  height: 12px;
  background: #E2E8F0;
  border-radius: 6px;
  margin-top: 10px;
}

.rp-sk__line--long {
  width: 80%;
}

.rp-sk__line--med {
  width: 55%;
}

@keyframes rp-shimmer {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

/* ── Section ── */
.rp-section {
  margin-bottom: 16px;
}

.rp-section--pack {
  margin-bottom: 20px;
}

.rp-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding: 0 2px;
}

.rp-section__title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 700;
  color: #1F2937;
}

.rp-section__note {
  font-size: 11px;
  color: #9CA3AF;
  background: white;
  padding: 2px 10px;
  border-radius: 10px;
  border: 1px solid #E2E8F0;
}

/* ── Card ── */
.rp-card {
  background: white;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

/* ── Read-only rows ── */
.rp-field-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 16px;
  border-bottom: 1px solid #F3F4F6;
}

.rp-field-row--noborder {
  border-bottom: none;
}

.rp-field-row--disabled {
  background: #FAFAFA;
}

.rp-field-lbl {
  font-size: 13px;
  color: #9CA3AF;
  font-weight: 500;
  flex-shrink: 0;
  min-width: 100px;
}

.rp-field-val {
  font-size: 14px;
  font-weight: 600;
  color: #1F2937;
  text-align: right;
  flex: 1;
}

.rp-field-val--sm {
  font-size: 13px;
}

.rp-plate {
  font-size: 13px;
  font-weight: 800;
  background: #0F172A;
  color: white;
  padding: 3px 12px;
  border-radius: 6px;
  letter-spacing: 1px;
}

/* Divider inside card */
.rp-divider {
  height: 1px;
  background: #E2E8F0;
  margin: 4px 0;
}

/* ── Editable inputs ── */
.rp-field-input {
  padding: 14px 16px;
  border-bottom: 1px solid #F3F4F6;
}

.rp-field-input--noborder {
  border-bottom: none;
}

.rp-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.rp-input {
  width: 100%;
  font-size: 15px;
  font-weight: 600;
  color: #1F2937;
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 10px;
  padding: 10px 14px;
  font-family: 'Sarabun', 'Noto Sans Thai', sans-serif;
  outline: none;
  transition: border-color 0.2s;
}

.rp-input:focus {
  border-color: #2563EB;
  background: white;
}

.rp-select {
  appearance: none;
  cursor: pointer;
}

/* Save button */
.rp-save-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(100% - 32px);
  margin: 16px;
  padding: 12px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 700;
  font-family: 'Sarabun', 'Noto Sans Thai', sans-serif;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background: linear-gradient(135deg, #1D4ED8, #2563EB);
  color: white;
  box-shadow: 0 4px 14px rgba(29, 78, 216, 0.3);
}

.rp-save-btn:active {
  transform: scale(0.97);
}

.rp-save-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.rp-save-btn--green {
  background: linear-gradient(135deg, #059669, #10B981);
  box-shadow: 0 4px 14px rgba(5, 150, 105, 0.3);
}

/* Empty row */
.rp-empty-row {
  text-align: center;
  padding: 20px;
  font-size: 13px;
  color: #9CA3AF;
}

/* ── Reviews ── */
.rp-rating-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0 16px;
  border-bottom: 1px solid #F1F5F9;
  margin-bottom: 16px;
}

.rp-rating-big {
  font-size: 36px;
  font-weight: 800;
  color: #1F2937;
  line-height: 1;
}

.rp-rating-stars {
  display: flex;
  align-items: center;
  gap: 2px;
}

.rp-rating-count {
  font-size: 13px;
  color: #6B7280;
  margin-left: 4px;
}

.rp-review-item {
  padding: 14px 0;
  border-bottom: 1px solid #F1F5F9;
}

.rp-review-item--last {
  border-bottom: none;
}

.rp-review-top {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
}

.rp-review-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #F1F5F9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rp-review-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rp-review-name {
  font-size: 13px;
  font-weight: 700;
  color: #1F2937;
  margin-bottom: 3px;
}

.rp-review-stars {
  display: flex;
  align-items: center;
  gap: 2px;
}

.rp-review-date {
  font-size: 11px;
  color: #9CA3AF;
  margin-left: 6px;
}

.rp-review-comment {
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
  padding-left: 46px;
}
</style>
